/** Nom du paramètre qui transporte la destination de retour vers `/connexion`. */
export const RETURN_URL_PARAM = 'retour';

export const DEFAULT_RETURN_URL = '/';

/** Chemins qui ne doivent jamais être une destination de retour. */
const EXCLUDED_PREFIXES = ['/connexion', '/creer-un-compte', '/mot-de-passe-oublie'];

/**
 * Restreint la destination de retour à un chemin interne.
 *
 * Sans ce filtre, une valeur absolue transformerait le retour de connexion en
 * redirection ouverte : un lien piégé enverrait l'utilisateur, tout juste
 * authentifié, sur un site tiers imitant Champ'Table. On refuse aussi les
 * chemins protocole-relatifs `//hote`, que le navigateur traite comme externes.
 *
 * Les écrans d'authentification sont exclus : y revenir après s'être connecté
 * ferait boucler le parcours.
 */
export function sanitizeReturnUrl(candidate: string | null | undefined): string {
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//')) {
    return DEFAULT_RETURN_URL;
  }
  if (EXCLUDED_PREFIXES.some((prefix) => candidate.startsWith(prefix))) {
    return DEFAULT_RETURN_URL;
  }
  return candidate;
}

/**
 * URL de l'écran de connexion, mémorisant où revenir ensuite. À réserver aux
 * appels qui analysent la chaîne — `Router.parseUrl`, `navigateByUrl` — car
 * `routerLink` traiterait la partie interrogative comme un segment de chemin.
 */
export function signInUrl(returnUrl: string | undefined): string {
  const target = sanitizeReturnUrl(returnUrl);
  if (target === DEFAULT_RETURN_URL) {
    return '/connexion';
  }
  return `/connexion?${RETURN_URL_PARAM}=${encodeURIComponent(target)}`;
}

/** Même destination, sous la forme attendue par `routerLink`. */
export function signInParams(returnUrl: string | undefined): Record<string, string> {
  const target = sanitizeReturnUrl(returnUrl);
  return target === DEFAULT_RETURN_URL ? {} : { [RETURN_URL_PARAM]: target };
}
